// ─────────────────────────────────────────────────────────────────────────────
// SUMMER OFFER BANNER  — Drop-in replacement for the SummerBanner component
// Usage: same as before — <SummerBanner course={course} />
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Flame } from 'lucide-react';

const OFFER_END = new Date('2026-05-05T23:59:59');

function useCountdown(target) {
  const calc = () => {
    const diff = Math.max(0, target - Date.now());
    return {
      days:    Math.floor(diff / 86400000),
      hours:   Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000)  / 60000),
      seconds: Math.floor((diff % 60000)    / 1000),
      expired: diff === 0,
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

function FlipDigit({ value }) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    <motion.span
      key={value}
      initial={{ rotateX: -90, opacity: 0 }}
      animate={{ rotateX: 0, opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{ display: 'inline-block', transformOrigin: 'center' }}
    >
      {pad(value)}
    </motion.span>
  );
}

export default function SummerBanner({ course }) {
  const { days, hours, minutes, seconds, expired } = useCountdown(OFFER_END);
  const [visible, setVisible] = useState(true);

  if (expired || !visible) return null;

  const saving = Number(course.original_price) - Number(course.price);
  const pct    = Math.round((saving / Number(course.original_price)) * 100);
  const fmt    = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

  const units = [
    { label: 'Days',    value: days    },
    { label: 'Hours',   value: hours   },
    { label: 'Mins',    value: minutes },
    { label: 'Secs',    value: seconds },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1,  y: 0   }}
        exit={{    opacity: 0,  y: -20, scale: 0.97 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative mb-6 rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f0c29 0%, #1a1040 40%, #0f0c29 100%)',
          boxShadow: '0 0 0 1px rgba(255,180,0,0.15), 0 20px 60px rgba(0,0,0,0.5)',
        }}
      >

        {/* ── Glowing orbs in background ── */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div style={{
            position: 'absolute', top: '-40%', left: '-10%',
            width: '400px', height: '400px',
            background: 'radial-gradient(circle, rgba(255,140,0,0.18) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
          <div style={{
            position: 'absolute', bottom: '-30%', right: '5%',
            width: '320px', height: '320px',
            background: 'radial-gradient(circle, rgba(255,60,120,0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
          <div style={{
            position: 'absolute', top: '20%', right: '30%',
            width: '200px', height: '200px',
            background: 'radial-gradient(circle, rgba(100,80,255,0.12) 0%, transparent 70%)',
            borderRadius: '50%',
          }} />
        </div>

        {/* ── Subtle grid texture ── */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} />

        {/* ── Top accent line ── */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
          background: 'linear-gradient(90deg, transparent, #ffb400, #ff4d6d, #ffb400, transparent)',
        }} />

        {/* ── Close button ── */}
        <button
          onClick={() => setVisible(false)}
          className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="relative px-6 py-6 sm:px-8 sm:py-7">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">

            {/* ── LEFT: Badge + Headline ── */}
            <div className="flex-1 space-y-3">

              {/* Badge row */}
              <div className="flex flex-wrap items-center gap-2">
                <motion.div
                  animate={{ scale: [1, 1.04, 1] }}
                  transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: 'linear-gradient(135deg, #ffb400, #ff6b00)',
                    borderRadius: '999px', padding: '4px 14px',
                    fontSize: '11px', fontWeight: 700,
                    color: '#000', letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}
                >
                  <Flame className="w-3 h-3" />
                  Xceed Summer Offer
                </motion.div>

                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '999px', padding: '4px 12px',
                  fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.7)',
                  letterSpacing: '0.06em',
                }}>
                  <Zap className="w-3 h-3 text-yellow-400" />
                  Limited Time
                </div>
              </div>

              {/* Headline */}
              <div>
                <h3 style={{
                  fontFamily: "'Syne', 'Space Grotesk', sans-serif",
                  fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                  fontWeight: 800,
                  color: '#ffffff',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}>
                  <span style={{
                    background: 'linear-gradient(90deg, #ffb400, #ff6b35)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    {pct}% OFF
                  </span>{' '}
                  on this course
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    textDecoration: 'line-through',
                    color: 'rgba(255,255,255,0.3)',
                  }}>
                    {fmt(course.original_price)}
                  </span>
                  <span style={{
                    color: '#4ade80',
                    fontWeight: 600,
                    background: 'rgba(74,222,128,0.1)',
                    border: '1px solid rgba(74,222,128,0.2)',
                    borderRadius: '6px',
                    padding: '1px 8px',
                    fontSize: '0.78rem',
                  }}>
                    You save {fmt(saving)}
                  </span>
                </p>
              </div>

              {/* Progress bar */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                    🔥 Spots filling fast at this price
                  </span>
                  <span style={{ fontSize: '0.72rem', color: '#ffb400', fontWeight: 600 }}>
                    78% claimed
                  </span>
                </div>
                <div style={{
                  height: '5px', background: 'rgba(255,255,255,0.08)',
                  borderRadius: '999px', overflow: 'hidden',
                }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '78%' }}
                    transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    style={{
                      height: '100%',
                      background: 'linear-gradient(90deg, #ffb400, #ff4d6d)',
                      borderRadius: '999px',
                      position: 'relative',
                    }}
                  >
                    {/* Shimmer effect */}
                    <motion.div
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ repeat: Infinity, duration: 2, delay: 1.5, ease: 'linear' }}
                      style={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        borderRadius: '999px',
                      }}
                    />
                  </motion.div>
                </div>
              </div>
            </div>

            {/* ── DIVIDER ── */}
            <div className="hidden lg:block" style={{
              width: '1px', alignSelf: 'stretch', minHeight: '80px',
              background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.1), transparent)',
            }} />

            {/* ── RIGHT: Countdown Timer ── */}
            <div className="flex flex-col items-start lg:items-center gap-3">
              <p style={{
                fontSize: '0.7rem', fontWeight: 700,
                color: 'rgba(255,255,255,0.35)',
                textTransform: 'uppercase', letterSpacing: '0.12em',
              }}>
                Offer Ends In
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {units.map(({ label, value }, i) => (
                  <React.Fragment key={label}>
                    {/* Digit block */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        padding: '10px 14px',
                        minWidth: '56px',
                        position: 'relative',
                        overflow: 'hidden',
                        backdropFilter: 'blur(8px)',
                      }}>
                        {/* Inner top glow */}
                        <div style={{
                          position: 'absolute', top: 0, left: '20%', right: '20%',
                          height: '1px',
                          background: 'linear-gradient(90deg, transparent, rgba(255,180,0,0.5), transparent)',
                        }} />
                        {/* Horizontal divider line (flip clock style) */}
                        <div style={{
                          position: 'absolute', top: '50%', left: 0, right: 0,
                          height: '1px', background: 'rgba(0,0,0,0.3)',
                        }} />
                        <span style={{
                          fontFamily: "'DM Mono', 'Courier New', monospace",
                          fontSize: '1.6rem',
                          fontWeight: 700,
                          color: '#ffffff',
                          lineHeight: 1,
                          display: 'block',
                          letterSpacing: '-0.02em',
                        }}>
                          <FlipDigit value={value} />
                        </span>
                      </div>
                      <p style={{
                        fontSize: '0.62rem', marginTop: '5px',
                        color: 'rgba(255,255,255,0.35)',
                        textTransform: 'uppercase', letterSpacing: '0.1em',
                        fontWeight: 600,
                      }}>
                        {label}
                      </p>
                    </div>

                    {/* Colon separator (not after last item) */}
                    {i < units.length - 1 && (
                      <motion.span
                        animate={{ opacity: [1, 0.2, 1] }}
                        transition={{ repeat: Infinity, duration: 1, ease: 'easeInOut' }}
                        style={{
                          fontSize: '1.4rem', fontWeight: 700,
                          color: 'rgba(255,180,0,0.6)',
                          marginBottom: '18px',
                          userSelect: 'none',
                        }}
                      >
                        :
                      </motion.span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ── Bottom accent line ── */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,180,0,0.2), rgba(255,77,109,0.2), transparent)',
        }} />
      </motion.div>
    </AnimatePresence>
  );
}